import { makeStyles, Button } from '@material-ui/core';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import RefreshIcon from '@material-ui/icons/Refresh';
import ClearAllIcon from '@material-ui/icons/ClearAll';
import { useSelector } from 'react-redux';

const useStyles = makeStyles(theme => ({
  container: {
    position: 'sticky',
    left: '50%',
    right: '50%',
    maxWidth: 1,
  },
  content: {
    position: 'absolute',
    width: 650,
    maxWidth: 720,
    height: 400,
    transform: 'translate(-50%, 40px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: '40px 80px',
    borderRadius: 10,
    border: '1px dashed #e0e0e0',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  iconContainer: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: 'rgba(220, 18, 18, 0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  icon: {
    fontSize: 20,
    color: theme.colors?.darkError || '#dc1212',
  },
  title: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.colors?.blue2 || '#122056',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  subtitle: {
    fontSize: 12,
    color: '#bbb',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    textAlign: 'center',
    lineHeight: 1.4,
  },
  validationList: {
    margin: 0,
    paddingLeft: 16,
    fontSize: 12,
    color: theme.colors?.darkError || '#dc1212',
    fontFamily: 'monospace',
    lineHeight: 1.6,
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: 4,
    width: '100%',
    maxWidth: 250,
  },
  retryBtn: {
    textTransform: 'none',
    fontSize: 12,
    fontWeight: 500,
    padding: '4px 14px',
    minHeight: 30,
  },
  resetBtn: {
    textTransform: 'none',
    fontSize: 12,
    fontWeight: 500,
    padding: '4px 14px',
    minHeight: 30,
    color: theme.colors?.grey || '#8E8E8E',
    borderColor: '#e0e0e0',
  },
}));

const getErrorSubtitle = (error, tables) => {
  if (error.validationErrors) {
    return null;
  }
  if (error.status >= 500) {
    return tables?.unexpectedServerError || 'An unexpected server error occurred';
  }
  return error.message || tables?.failedToLoadData || 'Failed to load data';
};

const ErrorFallback = ({ error, onRetry, hasActiveFilters }) => {
  const classes = useStyles();
  const {
    en: { tables, btn },
  } = useSelector(state => state.i18n);

  const subtitle = getErrorSubtitle(error, tables);
  const validationErrors = error?.validationErrors;

  const handleRetry = () => {
    if (onRetry) onRetry(true);
  };

  const handleRetryWithReset = () => {
    if (onRetry) onRetry(false);
  };

  return (
    <div className={`${classes.container} table-fallback`}>
      <div className={classes.content}>
        <div className={classes.header}>
          <div className={classes.iconContainer}>
            <ErrorOutlineIcon className={classes.icon} />
          </div>
          <div className={classes.title}>{tables?.unableToLoadData || 'Unable to load data'}</div>
        </div>

        {subtitle && <div className={classes.subtitle}>{subtitle}</div>}

        {validationErrors && (
          <ul className={classes.validationList}>
            {Object.entries(validationErrors).map(([field, message]) => (
              <li key={field}>{message}</li>
            ))}
          </ul>
        )}

        <div className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            className={classes.retryBtn}
            startIcon={<RefreshIcon style={{ fontSize: 16 }} />}
            onClick={handleRetry}
          >
            {btn?.retry || 'Retry'}
          </Button>
          {hasActiveFilters && (
            <Button
              variant="outlined"
              size="small"
              className={classes.resetBtn}
              startIcon={<ClearAllIcon style={{ fontSize: 16 }} />}
              onClick={handleRetryWithReset}
            >
              {btn?.retryAndClearFilters || 'Retry and clear filters'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorFallback;
